import classnames from 'classnames';
import { isEqual, isNull, isUndefined, max } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../../hooks/react-redux';
import { widthsByType } from '../../../../admin/dataset-builder/preview/tablePreview/TableHeader';
import { DatasetFieldSettings, DatasetFieldType, DatasetType, TableDatasetFieldType } from '../../../../datasets/store/types';
import { Toggle } from '../../../../shared/toggle';
import { CustomTooltip, InformationTooltip, OverflowTooltip } from '../../../../shared/tooltip';
import { addDocumentReportField, DocumentReportField, getReportDatasetDefinitions, getReportFields, getReportFieldsTableDatasetId, getReportMetaFields, removeDocumentReportField } from '../../store';
import styles from '../ReportDefinitions.module.scss';
import { getIsSelectedMetaField } from '../../store/utils';
import { Icon } from '../../../../shared/icon/Icon';
import { SmileyPositive } from '../../../../shared/icons';

const { white, green, lightGrey } = styles;

interface TableHeaderProps {
    id: string;
    label: string;
    description: string;
    refLabel?: string
    showRef?: boolean;
    type: TableDatasetFieldType;
    isFirstCell: boolean;
    datasetId: number;
    parentFieldId: string;
    settings: DatasetFieldSettings;
}

export const TableHeader: React.FC<TableHeaderProps> = ({ label, description, id, refLabel, showRef, type, isFirstCell, datasetId, parentFieldId, settings }) => {
    const dispatch = useAppDispatch();
    const datasetDefinitions = useAppSelector(getReportDatasetDefinitions);
    const reportFields = useAppSelector(getReportFields);
    const includedTableDatasetId = useAppSelector(getReportFieldsTableDatasetId);
    const selectedMetaFields = useAppSelector(getReportMetaFields);

    const cellWidth = useMemo(() => `${max([widthsByType[type], 150])}px`, [type]);
    const columnLabel = useMemo(() => label || 'Label...', [label]);

    const datasetDefinition = useMemo(() => datasetDefinitions.find(definition => definition.datasetId === datasetId), [datasetDefinitions, datasetId]);
    const fieldName = useMemo(() => datasetDefinition?.datasetTitle || '', [datasetDefinition]);

    const datasetTitle = useMemo(() => datasetDefinition?.datasetTitle || '', [datasetDefinition]);
    const isMetaFieldType = useMemo(() => type === DatasetFieldType.DOCUMENT, [type]);
    const isEnabledTableDatasetId = useMemo(() => isNull(includedTableDatasetId) || datasetId === includedTableDatasetId, [includedTableDatasetId, datasetId]);
    const toggleDisabled = useMemo(() => isMetaFieldType || !isEnabledTableDatasetId, [isMetaFieldType, isEnabledTableDatasetId]);

    const toggleDisabledTooltip = useMemo(() => {
        if (isMetaFieldType) {
            return ['This field can be added in the meta document fields tab'];
        }
        if (!isEnabledTableDatasetId) {
            return ['Currently we are unable to include multiple tables within a single document report'];
        }
        return null;
    }, [isMetaFieldType, isEnabledTableDatasetId]);

    const isSelectedMetaField = useMemo(() => type === DatasetFieldType.DOCUMENT && !isNull(settings.documentProperty) && !isUndefined(settings.documentProperty) && getIsSelectedMetaField(settings.documentProperty, selectedMetaFields), [type, settings.documentProperty, selectedMetaFields]);
    const documentReportField: DocumentReportField = useMemo(() => ({ datasetId, datasetTitle, datasetType: DatasetType.TABLE, fieldName, id: id!, label: label || '', parentFieldId, description, customLabel: '', includeDatasetTitle: fieldName !== label }), [id, label, parentFieldId, datasetId, datasetTitle, fieldName, description]);
    const isIncludedReportField = useMemo(() => !isUndefined(reportFields.find(reportField => isEqual(documentReportField, reportField))) || isSelectedMetaField, [reportFields, documentReportField, isSelectedMetaField]);

    const addReportField = useCallback(() => dispatch(addDocumentReportField(documentReportField)), [dispatch, documentReportField]);
    const removeReportField = useCallback(() => dispatch(removeDocumentReportField(id!)), [dispatch, id]);
    const toggleReportField = useCallback((checked: boolean) => checked ? addReportField() : removeReportField(), [removeReportField, addReportField]);

    const headerContent = useMemo(() => (
        <div className={styles.cellHeader}>
            <OverflowTooltip className={classnames(styles.headerLabel, { [styles.emptyHeaderLabel]: !label })} overlayText={columnLabel} testId={`report-table-header-${id}-label`} />
            <div className={styles.headerIconWrapper}>
                {description && <InformationTooltip content={description} labelColor={white} />}
                {refLabel && showRef && <InformationTooltip content={refLabel} label='?' labelColor={white} />}
                {settings.riskField && <CustomTooltip overlayText='Risk Tolerance is available on this field'><div className={styles.riskFieldWrapper}><Icon icon={SmileyPositive} fill={isIncludedReportField ? green : lightGrey} fontSize={22} /></div></CustomTooltip>}
                <Toggle onChange={toggleReportField} checked={isIncludedReportField} disabled={toggleDisabled} onColour={green} disabledTooltip={toggleDisabledTooltip} />
            </div>
        </div>
    ), [columnLabel, description, refLabel, showRef, label, id, isIncludedReportField, toggleReportField, toggleDisabled, toggleDisabledTooltip, settings.riskField]);

    return (
        <div
            className={classnames(styles.tableHeaderWrapper, {
                [styles.firstCell]: isFirstCell
            })}
            style={{ width: cellWidth, minWidth: cellWidth }}
            id={`document-report-field-${id}`}
        >
            {headerContent}
        </div>
    );
};
